import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { noop, deburr, escapeRegExp } from 'lodash';

import { omitProps, deriveStateFromWatchedProps } from 'utils/react';
import { TextHighlighter } from 'components/Misc';
import { LanguageContext } from 'containers/LanguageProvider';
import { findItemByValue } from 'components/Dropdown';
import { ClearableTextInput, TextInput } from 'components/Input';
import { RenderProp } from 'components/Utilities';
import { SelectContainer, FilteredSelectInput } from './components';
import { propTypes, defaultProps } from './propTypes';

function removeWhitespace(string) {
    return string.replace(/\s+/g, '');
}

export default class FilteredSelect extends Component {
    static displayName = 'FilteredSelect';

    static propTypes = {
        ...propTypes,
        name: PropTypes.string,
        allowAny: PropTypes.bool,
        clearable: PropTypes.bool,
        useHiddenInput: PropTypes.bool,
        inputComponent: PropTypes.func,
        highlightText: PropTypes.bool,
        selectAllContentClassName: PropTypes.string,
        clearLabel: PropTypes.string,
        emptyLabel: PropTypes.node.isRequired,
        valuesDenominator: PropTypes.string,
        onFilter: PropTypes.func,
        onChange: PropTypes.func,
        onClear: PropTypes.func,
        onClick: PropTypes.func,
        onBlur: PropTypes.func,
    };

    static contextType = LanguageContext;

    static defaultProps = {
        ...defaultProps,
        name: '',
        allowAny: false,
        clearable: false,
        useHiddenInput: true,
        inputComponent: TextInput,
        highlightText: false,
        selectAllContentClassName: null,
        clearLabel: null,
        valuesDenominator: ',',
        filterItems: null,
        renderItem: null,
        onFilter: noop,
        onChange: noop,
        onClear: noop,
        onClick: noop,
        onBlur: noop,
    };

    static getDerivedStateFromProps(props, state) {
        return deriveStateFromWatchedProps(props, state, {
            value: () => {
                const { items, value, allowAny } = props;
                const isItem = findItemByValue(items, value);
                const isString = value && typeof value === 'string';

                if (!isItem && allowAny && isString) {
                    return value;
                }
                return null;
            },
        });
    }

    state = {
        value: null,
        focused: false,
    };

    inputRef = createRef();

    selectContainerRef = createRef();

    getSearchValue() {
        const { value } = this.state;
        if (!value) return value;
        return deburr(value).toLowerCase();
    }

    getItemValue = (item) => {
        const { valuesDenominator } = this.props;

        if (Array.isArray(item)) {
            return item.map((itemIem) => itemIem.value).join(valuesDenominator);
        }

        return item.value;
    };

    itemToString = (item) => {
        const { itemToString, selectedItemsText } = this.props;
        const { getString } = this.context;

        if (Array.isArray(item)) {
            if (item.length > 1) {
                return getString('input.select.selectedItems', selectedItemsText, {
                    count: item.length,
                });
            }
            return itemToString(item[0]) || item[0].label;
        }

        return itemToString(item) || item.label;
    };

    filterItems = (items) => {
        const { filterItems } = this.props;
        const search = this.getSearchValue();

        const filterItemsFunction = filterItems || this.filterItemsLocal;
        const itemsFiltered = filterItemsFunction(items, search);

        if (itemsFiltered.length > 0) {
            return itemsFiltered;
        }

        return [];
    };

    filterItemsLocal = (items, search) => {
        if (!search) return items;

        const searchWithoutSpaces = removeWhitespace(search);
        const regex = new RegExp(escapeRegExp(searchWithoutSpaces), 'i');

        return items.filter((item) => {
            const string = removeWhitespace(deburr(this.itemToString(item)));
            return regex.test(string);
        });
    };

    tryToSelectValue(value) {
        const { items } = this.props;

        const matchingItem = findItemByValue(items, value);
        if (matchingItem) {
            this.selectContainerRef.current?.selectItem(matchingItem);
            this.setState({ value: null });
        }
    }

    handleChange = (event) => {
        const { onFilter, onChange } = this.props;
        const { value } = event.target;

        this.setState({ value });
        this.selectContainerRef.current?.resetHighlightedItem();

        onFilter(value);
        onChange(event);
    };

    handleClear = (event) => {
        const { onClear } = this.props;
        const { value } = this.state;

        if (value !== null) {
            this.setState({ value: null });
        }
        this.selectContainerRef.current?.resetSelection();
        this.inputRef.current?.focus();

        onClear(event);
    };

    handleClick = (event) => {
        const { onClick } = this.props;

        this.selectContainerRef.current?.openDropdown();
        onClick(event);
    };

    handleFocus = (event) => {
        const { onFocus } = this.props;

        this.setState({ focused: true });
        onFocus(event);
    };

    handleBlur = (event) => {
        const { onBlur } = this.props;

        this.setState({ focused: false });
        onBlur(event);
    };

    handleSelect = (selectedItem, index) => {
        const { allowAny, onSelect } = this.props;

        if (allowAny) {
            this.setState({ value: null });
        }

        this.inputRef.current?.blur();
        onSelect(selectedItem, index);
    };

    handleClose = (...args) => {
        const { allowAny, onClose } = this.props;
        const { value } = this.state;

        if (allowAny && value !== null) {
            this.selectContainerRef.current?.resetSelection();
            this.tryToSelectValue(value);
        }
        if (!allowAny && value !== null) {
            this.setState({ value: null });
        }
        onClose(...args);
    };

    renderItemLocal = ({ item, search }) => {
        const { highlightText } = this.props;
        const { label, disabled } = item;

        if (disabled || !highlightText) return label;

        return <TextHighlighter tag="span" text={label} highlight={search} />;
    };

    renderItem = ({ item }) => {
        const { renderItem } = this.props;
        const search = this.getSearchValue();

        return (
            <RenderProp render={renderItem} argument={{ item, search }}>
                {this.renderItemLocal}
            </RenderProp>
        );
    };

    renderToggle = ({ selection }) => {
        const { name, allowAny, clearable, useHiddenInput, clearLabel, inputComponent } =
            this.props;
        const { value, focused } = this.state;

        const isFiltering = focused && value !== null;
        const useAnyValue = allowAny && value !== null;
        const isSelected = Array.isArray(selection) ? selection.length > 0 : !!selection;
        const useSelected = (!isFiltering && isSelected) || (!focused && !allowAny);

        const selectedLabel = isSelected ? this.itemToString(selection) : null;
        const selectedValue = isSelected ? this.getItemValue(selection) : null;

        const inputValue = !useAnyValue && useSelected ? selectedLabel : value;
        const hiddenValue = !useAnyValue || useSelected ? selectedValue : value;

        const input = clearable ? (
            <ClearableTextInput
                input={inputComponent}
                showButton={isSelected || useAnyValue}
                clearLabel={clearLabel}
                onClear={this.handleClear}
            />
        ) : (
            inputComponent
        );

        return (
            <FilteredSelectInput
                name={name}
                input={input}
                value={hiddenValue || undefined}
                inputValue={inputValue}
                useHiddenInput={useHiddenInput}
                autoComplete="off"
            />
        );
    };

    render() {
        const { selectAllText, ...rest } = this.props;
        const { getString } = this.context;

        const omitted = [
            'allowAny',
            'clearable',
            'useHiddenInput',
            'inputComponent',
            'highlightText',
            'clearLabel',
            'onClear',
            'onFilter',
            'valuesDenominator',
        ];

        return (
            <SelectContainer
                {...omitProps(rest, omitted)}
                ref={this.selectContainerRef}
                openOnFocus
                openOnKeyDown
                ignoreNavigationKeys
                toggleRef={this.inputRef}
                toggleOnClick={false}
                toggleWithSpace={false}
                toggleWithEnter={false}
                selectWithChar={false}
                selectWithSpace={false}
                highlightWithChar={false}
                filterItems={this.filterItems}
                itemToString={this.itemToString}
                renderToggle={this.renderToggle}
                renderItem={this.renderItem}
                selectAllText={getString('input.select.selectAll', selectAllText)}
                role="combobox"
                aria-autocomplete="list"
                aria-readonly={false}
                onClick={this.handleClick}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                onSelect={this.handleSelect}
                onClose={this.handleClose}
            />
        );
    }
}
